import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Lock, Sparkles } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import RotatingScripture from '../components/RotatingScripture';
import Paywall from '../components/Paywall';
import { api } from '../utils/api';

// Helper: does this user need to subscribe to keep using the app?
const needsSubscriptionForUser = (u) => {
  if (!u) return false;
  const subActive = u.subscription && u.subscription.is_active === true;
  if (subActive) return false;
  const trialEnd = u.trial && u.trial.trial_end ? new Date(u.trial.trial_end) : null;
  const trialActive = u.trial && u.trial.is_trial === true && trialEnd && trialEnd > new Date();
  return !trialActive;
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expiredUser, setExpiredUser] = useState(null); // holds the just-signed-in user if their trial has ended
  const [subscribing, setSubscribing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const data = await login(email, password);
      const u = data?.user;

      // If the trial is over and there's no active subscription, hold the user
      // here on the Login page and surface a Subscribe CTA instead of dropping
      // them into a broken dashboard state.
      if (needsSubscriptionForUser(u)) {
        setExpiredUser(u);
        toast('Your free trial has ended', {
          description: 'Subscribe to continue capturing spiritual downloads.',
        });
        return;
      }

      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      // Route through real Stripe Checkout instead of the mock activation.
      const { url } = await api.createStripeCheckoutSession(window.location.origin);
      if (!url) throw new Error('No checkout URL returned');
      window.location.href = url;
    } catch (e) {
      toast.error('Could not start checkout', { description: e?.message });
      setSubscribing(false);
    }
  };

  const handleRestore = async () => {
    setSubscribing(true);
    try {
      const res = await api.restoreStripePurchases();
      if (res?.restored) {
        toast.success('Subscription restored', {
          description: 'Welcome back — full access is unlocked.',
        });
        setTimeout(() => navigate('/dashboard'), 900);
      } else {
        toast(res?.message || 'No active subscription found for this email.', {
          description: 'You can subscribe below to continue.',
        });
      }
    } catch (e) {
      toast.error('Restore failed', { description: e?.message });
    } finally {
      setSubscribing(false);
    }
  };

  const handleBackToSignIn = () => {
    setExpiredUser(null);
    setPassword('');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        {/* Left side - Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 flex flex-col justify-center px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* App Title */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles size={40} className="text-[var(--sd-primary)]" />
                <h1 className="text-5xl font-brand text-[var(--sd-fg)]">
                  Selah Downloads™
                </h1>
              </div>
              <p className="text-xl text-[var(--sd-muted-fg)] italic font-scripture pl-14">
                Pause. Listen. Receive.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-semibold mb-4 text-[var(--sd-fg)]">
                Welcome back
              </h2>
              <p className="text-lg text-[var(--sd-muted-fg)] leading-relaxed">
                Continue your spiritual journey. Your insights, prayers, and divine moments await.
              </p>
            </div>

            {/* Bible Verse (rotates) */}
            <div className="mb-6">
              <RotatingScripture
                scriptureKey="login-hero"
                mood="rest"
                variant="card"
                center={false}
                className="p-6"
              />
            </div>

            <div className="privacy-badge inline-flex">
              <Lock size={16} />
              <span>Your entries are private. Encryption is built in.</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-[var(--sd-bg)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          {/* Mobile Title */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles size={32} className="text-[var(--sd-primary)]" />
              <h1 className="text-3xl font-brand text-[var(--sd-fg)]">
                Selah Downloads™
              </h1>
            </div>
            <p className="text-sm text-[var(--sd-muted-fg)] italic font-scripture">
              Pause. Listen. Receive.
            </p>
          </div>

          <Card className="glass-card p-8" style={expiredUser ? { background: '#121212', border: '1px solid rgba(253,251,247,0.08)' } : undefined}>
            {expiredUser ? (
              // Trial ended → real Stripe paywall (night-mode $7.77/mo)
              <div data-testid="login-subscribe-panel" className="space-y-4">
                <p className="text-[11px] uppercase tracking-[0.32em] text-center" style={{ color: '#D2B48C' }}>
                  Welcome back{expiredUser?.name ? `, ${expiredUser.name.split(' ')[0]}` : ''}
                </p>
                <p className="text-[13px] text-center leading-relaxed" style={{ color: 'rgba(253,251,247,0.72)' }}>
                  Your 7-day trial for <span style={{ color: '#FDFBF7' }}>{expiredUser?.email || 'this account'}</span> has ended.
                  Your encrypted downloads and history are safely waiting.
                </p>

                <Paywall variant="embedded" />

                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className="mt-2 w-full text-xs underline"
                  style={{ color: 'rgba(253,251,247,0.52)' }}
                  data-testid="login-back-to-signin"
                >
                  Use a different account
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold mb-2">Sign in</h2>
                  <p className="text-sm text-[var(--sd-muted-fg)]">
                    Enter your credentials to access your downloads
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      disabled={isLoading}
                      data-testid="login-email-input"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="password" className="block text-sm font-medium">
                        Password
                      </label>
                      <Link
                        to="/forgot-password"
                        data-testid="login-forgot-password-link"
                        className="text-xs text-[var(--sd-primary)] hover:underline font-medium"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      disabled={isLoading}
                      data-testid="login-password-input"
                      className="w-full"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="login-submit-button"
                    className="w-full bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold"
                  >
                    {isLoading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-[var(--sd-muted-fg)]">
                    Don&apos;t have an account?{' '}
                    <Link
                      to="/register"
                      className="text-[var(--sd-primary)] hover:underline font-medium"
                      data-testid="login-register-link"
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </>
            )}
          </Card>

          {/* Mobile Bible Verse (rotates) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="lg:hidden mt-6"
          >
            <RotatingScripture
              scriptureKey="login-mobile"
              mood="rest"
              variant="compact"
            />
          </motion.div>
        </motion.div>
      </div>
      </div>

      <Disclaimer />
    </div>
  );
};

export default Login;